/*
 * The policy editor (docked, not a dialog): rules a formation follows on
 * its own. A preset to start from, the rules as rows (when → then), the
 * same policy as YAML, its cost against the budget as the server reckons
 * it, and Attach / Detach for the selected units.
 */

import { GoodIcon } from "./RecipePicker";
import { For, Index, Show, createEffect, createRoot, createSignal, on, type JSX } from "solid-js";
import { createStore, reconcile, unwrap } from "solid-js/store";
import { render } from "solid-js/web";
import {
  BARRACKS_ACTIONS,
  BARRACKS_LEAVES,
  goodsForPolicy,
  UNIT_ACTIONS,
  barracksPresets,
  blankBarracks,
  setDesignNames,
  ACTION_WORDS,
  FIRE_WORDS,
  TARGET_WORDS,
  LEAF_KINDS,
  LEAF_WORDS,
  MAX_RADIUS,
  MOVE_WORDS,
  PRESETS,
  TICKS_PER_SECOND,
  actionKind,
  blank,
  clausesOf,
  condOf,
  condText,
  errorText,
  leafKind,
  newAction,
  newLeaf,
  policyFromYaml,
  toYaml,
  type Action,
  type ActionKind,
  type Baked,
  type Clauses,
  type Fire,
  type Target,
  type Leaf,
  type LeafKind,
  type Moves,
  type Policy,
} from "../state/policy";

/** What Attach would apply to: the selection, and the policies already on
 *  its formations (by name). */
export interface PolicyTarget {
  units: number;
  attached: string[];
  /** Units (formations), or barracks (v31.7): what conditions and actions
   *  make sense, and the presets. */
  kind?: "units" | "barracks";
  /** A barracks' designs (its Make action's choices). */
  designs?: { id: number; name: string }[];
}

const [open, setOpen] = createSignal(false);
const [target, setTarget] = createSignal<PolicyTarget>({ units: 0, attached: [] });
export { open as policyEditorOpen };
export function setPolicyTarget(t: PolicyTarget): void {
  if (t.designs) setDesignNames(t.designs);
  setTarget(t);
}
const forBarracks = () => target().kind === "barracks";
const presets = () => (forBarracks() ? barracksPresets(target().designs ?? []) : PRESETS);

let handlers = {
  bake: (_yaml: string): Promise<Baked> => Promise.resolve({ ok: false, budget: 200, error: "not connected" }),
  attach: (_yaml: string | null) => {},
  changed: () => {},
};
export function setPolicyHandlers(h: typeof handlers): void {
  handlers = h;
}

// Kept across opening and closing (and the App rebuilt): an edit isn't lost.
const [policy, setPolicyStore] = createStore<Policy>(structuredClone(PRESETS[0][1]));
const [tab, setTab] = createSignal<"rules" | "yaml">("rules");
const [text, setText] = createSignal("");
const [baked, setBaked] = createSignal<(Baked & { for: string }) | null>(null);
const [preset, setPreset] = createSignal("0");

const setPolicy = (p: Policy) => setPolicyStore(reconcile(structuredClone(p)));
const edit = (f: (p: Policy) => void) => {
  const next = structuredClone(unwrap(policy)) as Policy;
  f(next);
  setPolicy(next);
  setPreset("");
};

/** Opens the editor: on `p` if given (the selection's policy), else on
 *  what was being edited. */
export function openPolicyEditor(p?: Policy): void {
  if (p) {
    setPolicy(p);
    setPreset("");
    setText(toYaml(p));
  }
  setOpen(true);
  handlers.changed();
}
export function closePolicyEditor(): void {
  setOpen(false);
  handlers.changed();
}

/** What's being checked: the rows, or the YAML as typed. */
const source = () => (tab() === "yaml" ? text() : toYaml(policy));
let asked = 0;
let timer = 0;
createRoot(() =>
 createEffect(
  on([source, open], ([s, o]) => {
    if (!o) return;
    clearTimeout(timer);
    const n = ++asked;
    timer = window.setTimeout(() => {
      handlers.bake(s).then((r) => {
        if (n !== asked) return;
        setBaked({ ...r, for: s });
        // Typed YAML the server read: the rows follow it.
        if (r.ok && r.yaml && tab() === "yaml") {
          try {
            setPolicy(policyFromYaml(r.yaml));
          } catch {
            /* the rows keep what they had */
          }
        }
      });
    }, 250);
  }),
 ),
);

const int = (v: string, lo: number, hi: number) => Math.max(lo, Math.min(hi, Math.floor(Number(v) || 0)));
const EVERY: [number, string][] = [
  [300, "30 s"],
  [600, "1 min"],
  [1200, "2 min"],
  [3000, "5 min"],
];

function Num(p: { value: number; lo: number; hi: number; unit?: string; set: (n: number) => void; tip?: string }): JSX.Element {
  return (
    <span class="policy-num" data-tip={p.tip}>
      <input type="number" min={p.lo} max={p.hi} value={p.value} onChange={(e) => p.set(int(e.currentTarget.value, p.lo, p.hi))} />
      <Show when={p.unit}>
        <span class="muted small">{p.unit}</span>
      </Show>
    </span>
  );
}

function LeafFields(p: { leaf: Leaf; set: (l: Leaf) => void }): JSX.Element {
  const k = () => leafKind(p.leaf);
  // Optional: a field asked once more as the kind changes reads nothing.
  const field = <T extends object>(key: string) => (typeof p.leaf === "string" ? undefined : (p.leaf as unknown as Record<string, T>)[key]);
  return (
    <>
      <Show when={k() === "stock_below"}>
        <Num
          value={field<{ amount: number }>("stock_below")?.amount ?? 0}
          lo={0}
          hi={1000000}
          tip="How much"
          set={(n) => p.set({ stock_below: { good: field<{ good: number }>("stock_below")?.good ?? 5, amount: n } })}
        />
        <span class="policy-good">
          <GoodIcon good={goodsForPolicy()[field<{ good: number }>("stock_below")?.good ?? 5] ?? "Steel"} size={18} />
          <select onChange={(e) => p.set({ stock_below: { good: Number(e.currentTarget.value), amount: field<{ amount: number }>("stock_below")?.amount ?? 0 } })}>
            <For each={goodsForPolicy().map((name, id) => ({ id, name })).filter((g) => g.id > 0)}>
              {(g) => (
                <option value={String(g.id)} selected={g.id === (field<{ good: number }>("stock_below")?.good ?? 5)}>
                  {g.name}
                </option>
              )}
            </For>
          </select>
        </span>
      </Show>
      <Show when={k() === "enemies_within" || k() === "friends_within"}>
        <Num
          value={field<{ at_least: number }>(k())?.at_least ?? 1}
          lo={1}
          hi={9999}
          unit="or more, within"
          tip="How many"
          set={(n) => p.set({ [k()]: { ...field<object>(k()), at_least: n } } as Leaf)}
        />
        <Num
          value={field<{ radius: number }>(k())?.radius ?? 1}
          lo={1}
          hi={MAX_RADIUS}
          unit="hexes"
          tip={`How far from the formation's centre (up to ${MAX_RADIUS}); further costs more`}
          set={(n) => p.set({ [k()]: { ...field<object>(k()), radius: n } } as Leaf)}
        />
      </Show>
      <Show when={k() === "ammo_below" || k() === "strength_below" || k() === "health_below" || k() === "under_fire" || k() === "arrived"}>
        <Num value={field<{ pct: number }>(k())?.pct ?? 1} lo={1} hi={100} unit="%" set={(n) => p.set({ [k()]: { pct: n } } as Leaf)} />
      </Show>
    </>
  );
}

function ActionFields(p: { action: Action; set: (a: Action) => void }): JSX.Element {
  const a = () => (typeof p.action === "string" ? {} : p.action) as Record<string, Record<string, unknown> | undefined>;
  return (
    <>
      <Show when={actionKind(p.action) === "engage"}>
        <Num
          value={(a().engage?.radius as number) ?? 1}
          lo={1}
          hi={MAX_RADIUS}
          unit="hexes"
          tip="Go after enemy strength within this many hexes"
          set={(n) => p.set({ engage: { radius: n } })}
        />
      </Show>
      <Show when={actionKind(p.action) === "produce"}>
        <select value={String(a().produce?.design ?? "")} onChange={(e) => p.set({ produce: { design: Number(e.currentTarget.value) } })}>
          <For each={target().designs ?? []}>
            {(d) => (
              <option value={String(d.id)} selected={d.id === (a().produce?.design as number)}>
                {d.name}
              </option>
            )}
          </For>
        </select>
      </Show>
      <Show when={actionKind(p.action) === "limit"}>
        <Num
          value={(a().limit?.keep_steel as number) ?? 0}
          lo={0}
          hi={1000000}
          unit="steel kept"
          tip="Makes a unit only if its station keeps this much steel after paying (0: none)"
          set={(n) => p.set({ limit: { keep_steel: n, max_units: (a().limit?.max_units as number) ?? 0 } })}
        />
        <Num
          value={(a().limit?.max_units as number) ?? 0}
          lo={0}
          hi={100000}
          unit="of the design at most"
          tip="Stops while you have this many of its design alive (0: no cap)"
          set={(n) => p.set({ limit: { keep_steel: (a().limit?.keep_steel as number) ?? 0, max_units: n } })}
        />
      </Show>
      <Show when={actionKind(p.action) === "prefer"}>
        <select value={a().prefer?.target as string} onChange={(e) => p.set({ prefer: { target: e.currentTarget.value as Target } })}>
          <For each={TARGET_WORDS}>{([v, l]) => <option value={v}>{l}</option>}</For>
        </select>
      </Show>
      <Show when={actionKind(p.action) === "posture"}>
        <select value={a().posture?.fire as string} onChange={(e) => p.set({ posture: { ...(a().posture as { moves: Moves }), fire: e.currentTarget.value as Fire } })}>
          <For each={FIRE_WORDS}>{([v, l]) => <option value={v}>{l}</option>}</For>
        </select>
        <select value={a().posture?.moves as string} onChange={(e) => p.set({ posture: { ...(a().posture as { fire: Fire }), moves: e.currentTarget.value as Moves } })}>
          <For each={MOVE_WORDS}>{([v, l]) => <option value={v}>{l}</option>}</For>
        </select>
      </Show>
    </>
  );
}

function RuleRow(p: { i: number }): JSX.Element {
  const rule = () => policy.rules[p.i];
  // Read through the store (a plain copy), so a change to it is seen.
  const clauses = () => clausesOf(JSON.parse(JSON.stringify(rule().when)));
  const setClauses = (k: Clauses) => edit((x) => (x.rules[p.i].when = condOf(k)));
  const setThen = (f: (t: Action[]) => void) => edit((x) => f(x.rules[p.i].then));
  return (
    <li class="policy-rule">
      <div class="policy-rule-head">
        <b>Rule {p.i + 1}</b>
        <label class="muted small" data-tip="Of the rules that hold, the highest priority acts (ties: the first written). It acts once, not again while it keeps holding.">
          Priority
          <input type="number" value={rule().priority} onChange={(e) => edit((x) => (x.rules[p.i].priority = int(e.currentTarget.value, -999, 999)))} />
        </label>
        <button class="ghost icon" aria-label={`Remove rule ${p.i + 1}`} data-tip="Remove this rule" disabled={policy.rules.length <= 1} onClick={() => edit((x) => x.rules.splice(p.i, 1))}>
          ×
        </button>
      </div>
      <div class="policy-part">
        <span class="row-label">When</span>
        <Show when={clauses()} fallback={<code class="policy-custom" data-tip="Nested deeper than rows can show: change it in the YAML tab">{condText(rule().when)}</code>}>
          {(k) => (
            <div class="policy-lines">
              <Show when={k().rows.length > 1}>
                <select class="policy-join" value={k().join} onChange={(e) => setClauses({ ...k(), join: e.currentTarget.value as "all" | "any" })}>
                  <option value="all">All of these</option>
                  <option value="any">Any of these</option>
                </select>
              </Show>
              <Index each={k().rows}>
                {(row, j) => (
                  <div class="policy-line">
                    <label class="policy-not" data-tip="The opposite: holds when this doesn't">
                      <input
                        type="checkbox"
                        checked={row().not}
                        onChange={(e) => setClauses({ ...k(), rows: k().rows.map((r, m) => (m === j ? { ...r, not: e.currentTarget.checked } : r)) })}
                      />
                      not
                    </label>
                    <select
                      value={leafKind(row().leaf)}
                      data-tip={LEAF_WORDS[leafKind(row().leaf)][1]}
                      onChange={(e) => setClauses({ ...k(), rows: k().rows.map((r, m) => (m === j ? { ...r, leaf: newLeaf(e.currentTarget.value as LeafKind) } : r)) })}
                    >
                      <For each={forBarracks() ? [...BARRACKS_LEAVES] : [...LEAF_KINDS]}>{(lk) => <option value={lk}>{LEAF_WORDS[lk][0]}</option>}</For>
                    </select>
                    <LeafFields leaf={row().leaf} set={(l) => setClauses({ ...k(), rows: k().rows.map((r, m) => (m === j ? { ...r, leaf: l } : r)) })} />
                    <Show when={k().rows.length > 1}>
                      <button class="ghost icon" aria-label="Remove condition" onClick={() => setClauses({ ...k(), rows: k().rows.filter((_, m) => m !== j) })}>
                        ×
                      </button>
                    </Show>
                  </div>
                )}
              </Index>
              <button class="ghost small add" onClick={() => setClauses({ ...k(), rows: [...k().rows, { not: false, leaf: newLeaf("enemies_within") }] })}>
                + Condition
              </button>
            </div>
          )}
        </Show>
      </div>
      <div class="policy-part">
        <span class="row-label">Then</span>
        <div class="policy-lines">
          <Index each={rule().then}>
            {(act, j) => (
              <div class="policy-line">
                <select value={actionKind(act())} data-tip={ACTION_WORDS[actionKind(act())][1]} onChange={(e) => setThen((t) => (t[j] = newAction(e.currentTarget.value as ActionKind)))}>
                  <For each={forBarracks() ? [...BARRACKS_ACTIONS] : [...UNIT_ACTIONS]}>{(ak) => <option value={ak}>{ACTION_WORDS[ak][0]}</option>}</For>
                </select>
                <ActionFields action={act()} set={(a) => setThen((t) => (t[j] = a))} />
                <Show when={rule().then.length > 1}>
                  <button class="ghost icon" aria-label="Remove action" onClick={() => setThen((t) => t.splice(j, 1))}>
                    ×
                  </button>
                </Show>
              </div>
            )}
          </Index>
          <button class="ghost small add" onClick={() => setThen((t) => t.push(newAction(forBarracks() ? "produce" : "halt")))}>
            + Action
          </button>
        </div>
      </div>
    </li>
  );
}

function PolicyPanel(): JSX.Element {
  const b = () => baked();
  // Checked, and what's shown is what was checked (no attaching mid-edit).
  const fresh = () => !!b()?.ok && b()!.for === source();
  const pct = () => Math.min(100, (100 * (b()?.cost ?? 0)) / (b()?.budget || 200));
  const choosePreset = (v: string) => {
    setPreset(v);
    if (v === "blank") setPolicy(forBarracks() ? blankBarracks() : blank());
    else if (v !== "") setPolicy(presets()[Number(v)][1]);
    setText(toYaml(policy));
  };
  const showTab = async (t: "rules" | "yaml") => {
    if (t === "yaml") {
      if (tab() !== "yaml") setText(toYaml(policy));
      return setTab(t);
    }
    // Back to the rows: they take the YAML as the server reads it; YAML it
    // can't read stays up, its error shown.
    if (tab() === "yaml") {
      const r = await handlers.bake(text());
      if (!r.ok || !r.yaml) return setBaked({ ...r, for: text() });
      try {
        setPolicy(policyFromYaml(r.yaml));
      } catch {
        return;
      }
    }
    setTab("rules");
  };
  return (
    <Show when={open()}>
      <aside
        class="plan-card policy-card"
        aria-label="Policy"
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            closePolicyEditor();
          }
        }}
      >
        <h2>Policy</h2>
        <label class="plan-option">
          Start from
          <select value={preset()} onChange={(e) => choosePreset(e.currentTarget.value)}>
            <option value="">Custom</option>
            <For each={presets()}>
              {([label], i) => (
                <option value={String(i())} selected={String(i()) === preset()}>
                  {label}
                </option>
              )}
            </For>
            <option value="blank">A blank rule</option>
          </select>
        </label>
        <div class="policy-meta">
          <label class="plan-option">
            Name
            <input type="text" value={policy.name} maxLength={60} onChange={(e) => edit((x) => (x.name = e.currentTarget.value.trim() || "Policy"))} />
          </label>
          <label class="plan-option" data-tip="How often its rules are checked; less often costs less">
            Check every
            <select value={String(EVERY.find(([t]) => t >= policy.every)?.[0] ?? policy.every)} onChange={(e) => edit((x) => (x.every = Number(e.currentTarget.value)))}>
              <For each={EVERY}>{([t, l]) => <option value={String(t)}>{l}</option>}</For>
              <Show when={!EVERY.some(([t]) => t === policy.every)}>
                <option value={String(policy.every)}>{Math.round(policy.every / TICKS_PER_SECOND)} s</option>
              </Show>
            </select>
          </label>
        </div>
        <div class="segmented" role="radiogroup" aria-label="View">
          <button role="radio" aria-checked={tab() === "rules"} onClick={() => showTab("rules")}>
            Rules
          </button>
          <button role="radio" aria-checked={tab() === "yaml"} onClick={() => showTab("yaml")}>
            YAML
          </button>
        </div>
        <Show
          when={tab() === "rules"}
          fallback={<textarea class="policy-yaml" spellcheck={false} value={text()} onInput={(e) => setText(e.currentTarget.value)} aria-label="Policy as YAML" />}
        >
          <ol class="policy-rules">
            <Index each={policy.rules}>{(_, i) => <RuleRow i={i} />}</Index>
          </ol>
          <button class="ghost small add" disabled={policy.rules.length >= 32} onClick={() => edit((x) => x.rules.push({ priority: 0, when: "always", then: [newAction(forBarracks() ? "produce" : "halt")] }))}>
            + Rule
          </button>
        </Show>
        <div
          class="policy-cost"
          classList={{ over: !!b() && !b()!.ok }}
          data-tip={`Its worst-case work each 30 s: bigger radii cost more, checking less often costs less. Over ${b()?.budget ?? 200} is refused.`}
        >
          <div class="bar">
            <div style={{ width: `${b() && !b()!.ok ? 100 : pct()}%` }} />
          </div>
          <span class="small">{b()?.ok ? `Cost ${b()!.cost} of ${b()!.budget}` : b() ? "Can't attach" : "Checking…"}</span>
        </div>
        <Show when={b() && !b()!.ok}>
          <p class="warn small">{errorText(b()!.error ?? "")}</p>
        </Show>
        <p class="muted small">
          <Show when={target().units} fallback="Select units (or barracks) to attach it to.">
            {target().units} {forBarracks() ? "barracks" : "units"} selected
            <Show when={target().attached.length}> · now: {[...new Set(target().attached)].join(", ")}</Show>
          </Show>
        </p>
        <div class="plan-actions">
          <button class="ghost" onClick={() => closePolicyEditor()}>
            Close
          </button>
          <Show when={target().attached.length}>
            <button class="ghost" onClick={() => handlers.attach(null)}>
              Detach
            </button>
          </Show>
          <button
            class="primary"
            aria-disabled={!fresh() || !target().units ? "true" : undefined}
            data-tip={!target().units ? "Select units first" : !fresh() ? "Fix it first (see above)" : undefined}
            onClick={() => fresh() && target().units && handlers.attach(source())}
          >
            Attach
          </button>
        </div>
      </aside>
    </Show>
  );
}

const host = document.createElement("div");
host.id = "policy-editor";
document.body.append(host);
render(() => <PolicyPanel />, host);
